import * as React from "react";
import * as _ from "lodash";
import styled, { css } from "styled-components";
import { useHistory } from "react-router-dom";

import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import { Text } from "src/library/components/Text/Text";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Icons } from "src/library/components/Icon/types";
import {
  useSupportApi,
  SupportTicketWithType
} from "src/modules/support/api/support.api";
import { PaginatedParams } from "src/core/shared/api";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

import { SupportTicketCard } from "./SupportTicketCard";

interface OrderSupportTicketsTabProps {
  orderId: number;
}

/**
 * Component to display a list of support tickts by order
 */
export const OrderSupportTicketsTab: React.ReactFCWithChildren<OrderSupportTicketsTabProps> = ({ orderId }) => {
  const [ getTicketsres, getTicketsReq ] = useSupportApi("LIST_ORDER_TICKETS");
  const [ tickets, setTickets ] = React.useState<SupportTicketWithType[]>([]);
  const history = useHistory();
  const checkPermission = useCheckUserPermission();

  const [ pagination, setPagination ] = React.useState<PaginatedParams<SupportTicketWithType>>({
    limit: 20,
    offset: 0,
    sort: [ [ "createdAt", "DESC" ] ],
    page: 0
  });

  React.useEffect(() => {
    getTicketsReq({
      orderId,
      pagination
    });
  }, [
    getTicketsReq,
    orderId,
    pagination
  ]);

  // on fetch of new data, set it to the tickets state but ensure
  // there are no duplicates
  React.useEffect(() => {
    if (getTicketsres.data) {
      setTickets(existingTickets => {
        return _.uniqBy(_.concat(existingTickets, getTicketsres.data?.result || []), "id");
      });
    }
  }, [ getTicketsres ]);

  return (
    <OrderSupportTicketsTabWrapper>
      {getTicketsres.error ? (
        <ErrorNotice
          message={getTicketsres.error.message}
          type="error"
        />
      ) :
        (
          <>
            <div className="raise-ticket-wrapper">
              <Text
                fontSize="h4"
                className="ticket-title"
              >
                Report an issue with your order
              </Text>

              <Button
                disabled={!checkPermission("amend-order")}
                onClick={() => history.push(`/orders/${orderId}/raise-ticket`)}
              >
                Raise support ticket
              </Button>
            </div>

            {!!tickets.length && (
              <div>
                <Text
                  fontSize="h4"
                  className="ticket-title"
                >
                  Previous support tickets for this order
                </Text>

                {tickets.map(ticket => (
                  <SupportTicketCard
                    key={ticket.id}
                    ticket={ticket}
                    history={history}
                  />
                ))}
              </div>
            )}

            {getTicketsres.pending && (
              <LoadingSpinner
                type="flat"
                position="inline"
              />
            )}

            {getTicketsres.data && getTicketsres.data?.count > tickets.length && (
              <Grid.Bounds
                direction="vertical"
                align="center"
              >
                <Grid.Box
                  align="center"
                  className="more-button-wrapper"
                >
                  <Button
                    icon={Icons.CHEVRONDOWN}
                    iconPosition="right"
                    $buttonType="plain"
                    onClick={() => setPagination({
                      ...pagination,
                      offset: tickets.length
                    })}
                  >
                    Show more
                  </Button>
                </Grid.Box>
              </Grid.Bounds>
            )}
          </>
        )}
    </OrderSupportTicketsTabWrapper>
  );
};

const OrderSupportTicketsTabWrapper = styled.div`
  ${({ theme }) => css`
    .ticket-title {
      display: block;
      margin-bottom: ${theme.spacing.margins.xs};
    }
    .raise-ticket-wrapper {
      margin-bottom: ${theme.spacing.margins.xl};
    }

    .more-button-wrapper {
      display: flex;
      justify-content: center;
    }
  `}
`;
